<script setup lang="ts">
import { onMounted, ref } from 'vue'
import EmojiPicker from 'vue3-emoji-picker'
import { useChat } from 'ai/vue'
import ChatMessage from './ChatMessage.vue'
import type { MessageData } from './types'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const props = defineProps<{ api?: string }>()

const { append, messages } = useChat({
  api: props.api,
})

const preEl = ref<HTMLPreElement>()
const newLines = ref(0)
const textareaEl = ref<HTMLTextAreaElement>()
const textareaValue = ref<string>('')
const extraInputHeight = ref(0)
const extraBottomSpace = ref(0)
const emojiMenuOpen = ref(false)
const canReply = ref(false)
const messageReceived = ref(true)
const chatEl = ref<HTMLDivElement>()

const responses = ref<MessageData[]>([
  {
    isReply: undefined,
    messageContent: "👋 Hi there! I am Joash's AI Chatbot just ask me anything.",
    messageAvatar: undefined,
    aiInfo: false,
    isAfterMessage: false,
    isBeforeMessage: true,
    isLoadingMessage: false,
  },
])

function toggleEmojiMenu() {
  emojiMenuOpen.value = !emojiMenuOpen.value
}

function countNewLines() {
  if (textareaValue.value && textareaValue.value.trim().length > 0)
    canReply.value = true
  else
    canReply.value = false
  newLines.value = Math.floor((preEl.value!.offsetHeight - 55) / 19)
  extraInputHeight.value = Math.min(19 * (newLines.value < 0 ? 0 : newLines.value), 200)
  extraBottomSpace.value = Math.min(19 * (newLines.value < 0 ? 0 : newLines.value), 200 - 56)
}


function onSelectEmoji(emoji: any) {
  textareaValue.value = textareaValue.value + emoji.i
  countNewLines()
  toggleEmojiMenu()
}

async function sendMessage() {
  const message = textareaValue.value
  if (message.trim() !== '') {
    responses.value.push({
      isReply: true,
      messageContent: message,
      isBeforeMessage: true,
      isAfterMessage: true,
    })

    chatEl.value?.scrollTo(0, chatEl.value.scrollHeight)
    extraBottomSpace.value = 0;
    extraInputHeight.value = 0
    messageReceived.value = false
    canReply.value = false
    textareaValue.value = ''

    setTimeout(() => {
      responses.value.push({
        isBeforeMessage: true,
        isAfterMessage: true,
        isLoadingMessage: true,
      })
    }, 200)

    append({
      content: message,
      role: 'user',
    }).then(() => {
      responses.value.splice(responses.value.length - 1, 1, {
        messageContent: messages.value[messages.value.length - 1].content,
        isBeforeMessage: true,
        isAfterMessage: true,
      })

      messageReceived.value = true
    })
  }
}

onMounted(() => {
  textareaEl.value!.addEventListener('keypress', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && messageReceived.value === true) {
      e.preventDefault()
      sendMessage()
    }
  })
})
</script>

<template>
  <div :class="cn('conversation-wrapper', $attrs.class ?? '')">
    <div class="conversation-container">
      <div
        class="beeetle-conversation beeetle-container-animated"
        tabindex="-1" role="presentation"
      >
        <div class="beeetle-conversation-body beeetle-conversation-flex">
          <div
            ref="chatEl"
            class="beeetle-conversation-body-parts"
            aria-live="assertive"
            tabindex="0" aria-atomic="false"
            role="alert" :style="`bottom: ${extraBottomSpace + 56}px;`"
          >
            <div class="beeetle-conversation-body-main-group">
              <div class="chats-view-wrapper">
                <div
                  class="chats-view chat-enter"
                  style="transform: translateY(0px);"
                >
                  <div v-for="message, idx in responses" :key="idx">
                    <!-- chats -->
                    <ChatMessage v-bind="message" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Chat Text Area -->
        <div
          class="beeetle-textarea-wrapper textarea-enter"
        >
          <div class="beeetle-composer" :style="`height: ${56 + extraInputHeight}px;}`">
            <pre ref="preEl">{{ textareaValue }}</pre>
            <textarea
              ref="textareaEl"
              v-model="textareaValue" name="message"
              placeholder="Write a reply..." aria-label="Write a reply..."
              tabindex="0" @input="countNewLines"
            />
            <div class="beeetle-composer-buttons">
              <Popover :open="emojiMenuOpen" @update:open="toggleEmojiMenu">
                <PopoverTrigger as-child>
                  <button class="composer-button">
                    <div :class="cn(' opacity-100 hover:opacity-70 text-lg', emojiMenuOpen ? 'i-fluent-emoji-28-filled text-[#0072A3] ' : 'i-fluent-emoji-28-regular text-[#757575]')" />
                  </button>
                </PopoverTrigger>
                <PopoverContent class="w-80 h-[244px] m-0 px-0 bottom--2 right--10 z-2147483001 absolute" style="background-color: white; border: white;" side="top">
                  <div class="grid gap-4">
                    <EmojiPicker :native="true" @select="onSelectEmoji" />
                  </div>
                </PopoverContent>
              </Popover>
              <button v-if="canReply" class="composer-button text-[#0072A3] text-lg disabled:text-gray" :disabled="!messageReceived" @click="sendMessage">
                <div class="i-ic-sharp-send" />
              </button>
              <button v-else class="composer-button">
                <div :class="cn(' opacity-100 hover:opacity-70 text-lg i-mi-attachment text-[#757575]')" aria-label="attach image" />
              </button>
            </div>
          </div>
        </div>
        <!-- Chat Text area end -->
      </div>
    </div>
  </div>
</template>
